import { StandardTabsV2 } from '@cimplico/components';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

type WorkpaperTabsProps = {
    /** Callback with the index of the newly selected tab */
    onChange: (idx: number) => void;
    /** The index of the currently selected tab */
    selectedTabIndex: number;
};

const status = ['ALL', 'OPEN', 'CLOSED', 'YOUR-WORK', 'ARCHIVED'];

function WorkpapersTabs({ onChange, selectedTabIndex }: WorkpaperTabsProps): JSX.Element {
    const [search, setSearch] = useSearchParams();

    const tab = search.get('tab');

    /**
     * When a tab is selected, set its corresponding status in the url `?tab=${status}`
     */
    const handleChange = useCallback(
        function handleChange(idx: number): void {
            onChange(idx);
            setSearch(
                (s) => {
                    s.set('tab', (status[idx] ?? 'all').toLowerCase());
                    if (idx !== 0) {
                        s.delete('status');
                    }
                    return s;
                },
                { replace: true }
            );
        },
        [onChange, setSearch]
    );

    // When search changes set the tab index
    useEffect(() => {
        // If no tab is selected in the URL, it should rely on the selectedTabIndex instead of the URL
        if (!tab) return;

        const index = status.indexOf(tab.toUpperCase());
        if (index !== selectedTabIndex) {
            onChange(index);
        }
    }, [selectedTabIndex, tab, onChange]);

    return (
        <div className="border-b">
            <StandardTabsV2 name="tb-status" onChange={handleChange} selectedIndex={selectedTabIndex}>
                <StandardTabsV2.Tab label={`All`} />
                <StandardTabsV2.Tab label={`Open Work`} />
                <StandardTabsV2.Tab label={`Closed Work`} />
                <StandardTabsV2.Tab label={`Your Work`} />
                <StandardTabsV2.Tab label={`Archived Work`} />
            </StandardTabsV2>
        </div>
    );
}

export default WorkpapersTabs;
